import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);

  const fetchCredits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/packages/credits`, { withCredentials: true });
      setCredits(data);
    } catch {
      setCredits(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
    } catch (error) {
      setUser(false);
      setCredits(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      fetchCredits();
    }
  }, [user, fetchCredits]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMsg };
    }
  };

  const register = async (name, email, password, role = 'student') => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password, role },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
    } finally {
      setUser(false);
    }
  };

  const refreshUser = async () => {
    await checkAuth();
    await fetchCredits();
  };

  const hasPlatformAccess = credits?.has_platform_access || user?.subscription_status === 'active';
  const hasSessionCredits = credits?.session_credits === -1 || (credits?.session_credits || 0) > 0;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    credits,
    fetchCredits,
    isAuthenticated: !!user,
    isSubscribed: user?.subscription_status === 'active',
    hasPlatformAccess,
    hasSessionCredits,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
